<template>
  <div
    class="hrm_menu_block btn-group dropstart relative flex hrm_table_menu text-white gap-2 items-center justify-between  ">
    <!-- ✅ MOBILE: Show First N Inline -->
    <ul v-if="isMobile" class="flex">
      <component v-for="(item, index) in firstVisibleItems" :key="'first-' + index" :is="item" />
    </ul>

    <!-- ✅ ORIGINAL BUTTON -->
    <slot name="menuBtn flex items-center justify-center w-full">
      <!-- mobile button -->
      <button v-if="isMobile" type="button"
        class="text-white absolute right-5 mb-2 list-none cursor-pointer flex items-center rounded-xl bg-[#252729] text-hrmThemeTextColor justify-center rounded-md w-6 h-6"
        data-bs-toggle="dropdown" data-dropdown-trigger="hover" aria-expanded="false">
        <Icon icon="ph:dots-three-vertical-bold" width="16" height="16" />
      </button>

      <!-- desktop button -->
      <button v-else type="button"
        class=" flex items-center justify-center btn btn-secondary h-6 w-6 theme-dropdown-btn" data-bs-toggle="dropdown"
        data-dropdown-trigger="hover" aria-expanded="false">
        <Icon icon="gridicons:menus" width="20" height="20" color="white" />
      </button>
    </slot>

    <!-- ✅ DROPDOWN -->
    <div class="dropdown-menu theme-dropdown-menu rounded-2xl text-white p-0.5 hrm_dropdown_menu_items">
      <!-- mobile: remaining items -->
      <ul v-if="isMobile">
        <component v-for="(item, index) in remainingItems" :key="'mob-' + index" :is="item" />
      </ul>

      <!-- desktop: original slot -->
      <template v-else>
        <slot name="menuItems"></slot>
      </template>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";

export default {
  name: "TableMenu",
  components: { Icon },

  props: {
    visibleCount: {
      type: Number,
      default: 2, // ✅ how many to show inline on mobile
    },
    mobileBreakpoint: {
      type: Number,
      default: 768,
    },
  },

  data() {
    return {
      isMobile: false,
    };
  },

  computed: {
    // ✅ robust slot extraction
    rawSlotItems() {
      if (!this.$slots.menuItems) return [];

      const slotFn = this.$slots.menuItems;
      const nodes = typeof slotFn === "function" ? slotFn() : slotFn;

      const result = [];

      const walk = (vnodes) => {
        (vnodes || []).forEach((node) => {
          if (!node) return;

          // ✅ native <li>
          if (node.type === "li" || node.tag === "li") {
            result.push(node);
            return;
          }

          // ✅ fragments / templates
          if (Array.isArray(node.children)) {
            walk(node.children);
            return;
          }

          // ✅ component slots (e.g., Inertia Link wrappers)
          if (
            node.children &&
            typeof node.children === "object" &&
            typeof node.children.default === "function"
          ) {
            walk(node.children.default());
          }
        });
      };

      walk(nodes);
      return result;
    },

    // ✅ first N visible inline (mobile)
    firstVisibleItems() {
      return this.rawSlotItems.slice(0, this.visibleCount);
    },

    // ✅ remaining items in dropdown (mobile)
    remainingItems() {
      return this.rawSlotItems.slice(this.visibleCount);
    },
  },

  methods: {
    checkScreen() {
      this.isMobile = window.matchMedia(
        `(max-width: ${this.mobileBreakpoint}px)`,
      ).matches;
    },
  },

  mounted() {
    this.checkScreen();
    window.addEventListener("resize", this.checkScreen);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreen);
  },
};
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.theme-dropdown-btn {
  border-radius: 6px !important;
  width: 26px;
  height: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hrm_table_menu .theme-dropdown-btn:hover {
  opacity: 0.6;
}

.dark .hrm_table_menu button.theme-dropdown-btn {
  background-color: #303337 !important;
}

.hrm_table_menu button.theme-dropdown-btn {
  background-color: #ebebeb !important;
}

.hrm_table_menu button.theme-dropdown-btn svg path {
  color: gray
}

.dark .hrm_table_menu .hrm_dropdown_menu_items {
  background-color: #24272a;
  padding: 4px 0;
  border: 1px solid #373d42 !important;
  box-shadow: 1px 0px 9px 1px #0b0c0d;
}

.hrm_table_menu .hrm_dropdown_menu_items {
  background-color: #f8f8f8;
  padding: 4px 0;
  border: 1px solid #dedede !important;
  box-shadow: 1px 0px 9px 1px #e1e1e1;
  border-radius: 4px;
}

.hrm_table_menu .hrm_dropdown_menu_items li.hrm_menu_item:not(.hrm_table_menu .hrm_dropdown_menu_items li.hrm_menu_item:last-child) {
  border-bottom: 1px solid #383d42;
}

@media (max-width: 768px) {


  .hrm_table_menu .theme-dropdown-btn:hover {
    opacity: 01;
  }

  .hrm_table_menu button.theme-dropdown-btn svg path {
    color: white
  }

}

.hrm_dropdown_menu_items {
  transition: 0.6s ease;
  animation: slideDown 0.6s forwards;
}

.hrm_dropdown_menu_items ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.hrm_dropdown_menu_items ul li.hrm_menu_item .hrm_menu_item_inner {
  font-size: 12px;
  font-weight: 600;
  display: flex;
  padding: 4px 16px;
  color: #727b84;
}

.hrm_dropdown_menu_items ul li.hrm_menu_item:hover .hrm_menu_item_inner {
  color: var(--hrm_Theme_Text_Color);
  background-color: var(--hrm_Theme_Bg_Color);
}

.hrm_dropdown_menu_items ul li.hrm_menu_item {
  cursor: pointer;
}

.hrm_table_menu>ul>li,
.hrm_table_menu>div>li {
  list-style: none !important;
  color: #369ffa;
  text-decoration: underline;
  padding-right: 6px;
  font-size: 13px;
  line-height: 1;
}

.hrm_table_menu>ul>li:not(:first-child),
.hrm_table_menu>div>li:not(:first-child) {
  border-left: 2px solid white;
  padding: 0px 6px;
}
</style>
